import type { Dispatch, SetStateAction } from "react";

import { LOOM_LOCAL_DEMO_PROJECT_ID } from "../../api/loomAuthoringAssistant";
import type { LoomExplicitTrimPreview } from "./loomExplicitTrimPreview";
import type { LoomNativeHybridAsset } from "./loomNativeHybridAsset";
import { LoomNativeInspectionPanel } from "./LoomNativeInspectionPanel";
import type { LoomNativeHybridLayers } from "./LoomNativeHybridViewer";
import { LoomNativeTruthPanel } from "./LoomNativeTruthPanel";
import { LoomPrimitiveAuthoringPanel, type LoomDefinitionRecordState } from "./LoomPrimitiveAuthoringPanel";
import type { LoomProvisionalUnionPreview } from "./loomProvisionalUnionPreview";
import { loomSurfaceStyleFromLayers, toggleLoomPresentationLayer,
  type LoomViewportPresentationState } from "./loomViewportPresentation";
import type { LoomAuthoringPreviewRequest } from "./useLoomAuthoringHistory";
import { useLoomAuthoringHistory } from "./useLoomAuthoringHistory";
import type { LoomAuthoringCommand } from "./loomAuthoringModel";

interface LoomTaskAdvancedAuthoringProps {
  readonly asset: LoomNativeHybridAsset | null;
  readonly authoring: ReturnType<typeof useLoomAuthoringHistory>;
  readonly definitionPreviewActive: boolean;
  readonly explicitTrimPreview: LoomExplicitTrimPreview | null;
  readonly extractionDirty: boolean;
  readonly geometryCurrent: boolean;
  readonly gpuUnionActive: boolean;
  readonly jacobianScale: number;
  readonly layers: LoomNativeHybridLayers;
  readonly onJacobianScaleChange: (scale: number) => void;
  readonly onLayersChange: Dispatch<SetStateAction<LoomNativeHybridLayers>>;
  readonly onParameterChange: (parameterId: string) => void;
  readonly onPreviewCommand: (command: LoomAuthoringCommand,
    request: LoomAuthoringPreviewRequest) => string | null;
  readonly onRecordDefinition: () => void;
  readonly onSelect: (nodeId: string) => void;
  readonly onViewportPresentationChange: Dispatch<SetStateAction<LoomViewportPresentationState>>;
  readonly parameterId: string;
  readonly persistedProject: boolean;
  readonly projectId: string;
  readonly provisionalUnionAvailable: boolean;
  readonly provisionalUnionPreview: LoomProvisionalUnionPreview | null;
  readonly recordState: LoomDefinitionRecordState;
  readonly selectedNodeId: string;
  readonly viewportPresentation: LoomViewportPresentationState;
}

export function LoomTaskAdvancedAuthoring({
  asset, authoring, definitionPreviewActive, explicitTrimPreview, extractionDirty,
  geometryCurrent, gpuUnionActive, jacobianScale, layers, onJacobianScaleChange,
  onLayersChange, onParameterChange, onPreviewCommand, onRecordDefinition, onSelect,
  onViewportPresentationChange, parameterId, persistedProject, projectId,
  provisionalUnionAvailable, provisionalUnionPreview, recordState, selectedNodeId,
  viewportPresentation,
}: LoomTaskAdvancedAuthoringProps) {
  const explicitTrimAvailable = explicitTrimPreview?.kind === "available";
  const displayedSurface = explicitTrimPreview?.kind === "available" ? explicitTrimPreview
    : gpuUnionActive ? null : provisionalUnionPreview?.kind === "available"
      ? provisionalUnionPreview : null;
  const sourceFixtureOverlayAvailable = explicitTrimPreview?.kind === "available"
    && explicitTrimPreview.method === "reflected-pairwise-hybrid-base";
  const teKuttaPairVerified = geometryCurrent
    && asset?.header.panel.trailingEdgeKuttaPairVerified === true;
  const aeroValidated = geometryCurrent && asset?.header.panel.aeroValidation.passed === true;
  const toggleLayer = (name: keyof LoomNativeHybridLayers) => {
    if ((name === "trailingEdge" || name === "jacobian") && !sourceFixtureOverlayAvailable) return;
    onLayersChange((current) => {
      const next = toggleLoomPresentationLayer(current, name);
      if (next.surface || next.tessellation) onViewportPresentationChange((value) => ({
        ...value, surfaceStyle: loomSurfaceStyleFromLayers(next.surface, next.tessellation),
      }));
      return next;
    });
  };
  const inspection = <div className="loom-task-advanced-evidence">
    <LoomNativeInspectionPanel asset={geometryCurrent ? asset : null}
      jacobianScale={jacobianScale} layers={layers}
      onJacobianScaleChange={onJacobianScaleChange} onLayerToggle={toggleLayer}
      onParameterChange={(next) => {
        onParameterChange(next);
        onLayersChange((current) => ({ ...current, jacobian: true }));
      }} open parameterId={parameterId}
      sourceFixtureOverlayAvailable={sourceFixtureOverlayAvailable}
      teKuttaPairVerified={teKuttaPairVerified} viewportPresentation={viewportPresentation} />
    <LoomNativeTruthPanel aeroValidated={aeroValidated}
      asset={geometryCurrent ? asset : null} assetLoaded={asset !== null}
      definitionPreviewActive={definitionPreviewActive} displayedSurface={displayedSurface}
      explicitTrimAvailable={explicitTrimAvailable} explicitTrimPreview={explicitTrimPreview}
      gpuUnionActive={gpuUnionActive} open provisionalUnionAvailable={provisionalUnionAvailable}
      provisionalUnionPreview={provisionalUnionPreview} teKuttaPairVerified={teKuttaPairVerified} />
  </div>;
  return <div className="loom-task-advanced-surface" data-testid="loom-task-advanced-surface">
    <LoomPrimitiveAuthoringPanel
      assistantProjectId={persistedProject ? projectId : LOOM_LOCAL_DEMO_PROJECT_ID}
      authoringReadOnlyReason={authoring.authoringReadOnlyReason}
      document={authoring.document} extractionDirty={extractionDirty}
      hideProductTree hideTaskDuplicateControls history={authoring.history} inspection={inspection}
      lastCommand={authoring.lastCommand} onCommand={authoring.acceptCommand}
      onPreviewCancel={authoring.cancelPreview} onPreviewCommand={onPreviewCommand}
      onRedo={authoring.redo} onRecordDefinition={onRecordDefinition} onSelect={onSelect}
      onUndo={authoring.undo} onWorkspaceIntent={authoring.acceptWorkspaceIntent}
      onWorkspaceRedo={authoring.workspaceRedo} onWorkspaceUndo={authoring.workspaceUndo}
      persistenceWriteState={authoring.persistenceWriteState} recordState={recordState}
      selectedNodeId={selectedNodeId} storageStatus={authoring.storageStatus}
      workspace={authoring.workspace} workspaceHistory={authoring.workspaceHistory} />
  </div>;
}
